import type { OptionReorderScope } from "../../../shared/constants/api";
import { AppError } from "../../../shared/utils/error-handler";
import type { CompanyProductSystemRepository } from "../../repositories/company/company-product-system-repository";
import type {
  ProductConfigDto,
  CategoryDto,
  DeleteProductResult,
  ProductLightDto,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreateProductRequest,
  UpdateProductRequest,
  DuplicateProductRequest,
  ProductPaperDto,
  CreateProductPaperRequest,
  SizeTemplateDto,
  CreateSizeTemplateRequest,
  OptionDto,
  OptionAttributeDto,
  OptionAttributeValueDto,
  CreateOptionRequest,
  CreateOptionAttributeRequest,
  CreateOptionAttributeValueRequest,
  PriceCalculationRequest,
  PriceCalculationResult,
  PaperSpecDto,
  MaterialDto,
} from "../../types/company/company-product-system";

export class CompanyProductSystemUseCase {
  constructor(private repository: CompanyProductSystemRepository) {}

  async getProductConfig(companyId: number): Promise<ProductConfigDto> {
    try {
      return await this.repository.getProductConfig(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "상품 설정 정보를 불러오지 못했습니다.",
        "PRODUCT_CONFIG_GET_FAILED",
        error,
      );
    }
  }

  async getCategories(companyId: number): Promise<CategoryDto[]> {
    try {
      return await this.repository.getCategories(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "카테고리 목록을 불러오지 못했습니다.",
        "CATEGORIES_GET_FAILED",
        error,
      );
    }
  }

  async createCategory(
    companyId: number,
    request: CreateCategoryRequest,
  ): Promise<CategoryDto> {
    try {
      return await this.repository.createCategory(companyId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "카테고리를 생성하지 못했습니다.",
        "CATEGORY_CREATE_FAILED",
        error,
      );
    }
  }

  async updateCategory(
    companyId: number,
    categoryId: number,
    request: UpdateCategoryRequest,
  ): Promise<CategoryDto> {
    try {
      return await this.repository.updateCategory(
        companyId,
        categoryId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "카테고리를 수정하지 못했습니다.",
        "CATEGORY_UPDATE_FAILED",
        error,
      );
    }
  }

  async deleteCategory(
    companyId: number,
    categoryId: number,
  ): Promise<void> {
    try {
      return await this.repository.deleteCategory(companyId, categoryId);
    } catch (error) {
      throw new AppError(
        String(error) || "카테고리를 삭제하지 못했습니다.",
        "CATEGORY_DELETE_FAILED",
        error,
      );
    }
  }

  async bulkUpdateCategoryOrder(
    companyId: number,
    orders: Array<{ categoryId: number; displayOrder: number }>,
  ): Promise<CategoryDto[]> {
    try {
      return await this.repository.bulkUpdateCategoryOrder(companyId, orders);
    } catch (error) {
      throw new AppError(
        String(error) || "카테고리 순서를 변경하지 못했습니다.",
        "CATEGORY_ORDER_UPDATE_FAILED",
        error,
      );
    }
  }

  async bulkUpdateProductOrder(
    companyId: number,
    orders: Array<{ productId: number; displayOrder: number }>,
  ): Promise<void> {
    try {
      return await this.repository.bulkUpdateProductOrder(companyId, orders);
    } catch (error) {
      throw new AppError(
        String(error) || "상품 순서를 변경하지 못했습니다.",
        "PRODUCT_ORDER_UPDATE_FAILED",
        error,
      );
    }
  }

  async getProducts(companyId: number): Promise<ProductLightDto[]> {
    try {
      return await this.repository.getProducts(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "상품 목록을 불러오지 못했습니다.",
        "PRODUCTS_GET_FAILED",
        error,
      );
    }
  }

  async getProduct(companyId: number, productId: number): Promise<any> {
    try {
      return await this.repository.getProduct(companyId, productId);
    } catch (error) {
      throw new AppError(
        String(error) || "상품 정보를 불러오지 못했습니다.",
        "PRODUCT_GET_FAILED",
        error,
      );
    }
  }

  async createProduct(
    companyId: number,
    request: CreateProductRequest,
  ): Promise<any> {
    try {
      return await this.repository.createProduct(companyId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "상품을 생성하지 못했습니다.",
        "PRODUCT_CREATE_FAILED",
        error,
      );
    }
  }

  async updateProduct(
    companyId: number,
    productId: number,
    request: UpdateProductRequest,
  ): Promise<any> {
    try {
      return await this.repository.updateProduct(
        companyId,
        productId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "상품을 수정하지 못했습니다.",
        "PRODUCT_UPDATE_FAILED",
        error,
      );
    }
  }

  async duplicateProduct(
    companyId: number,
    productId: number,
    request: DuplicateProductRequest,
  ): Promise<any> {
    try {
      return await this.repository.duplicateProduct(
        companyId,
        productId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "상품을 복제하지 못했습니다.",
        "PRODUCT_DUPLICATE_FAILED",
        error,
      );
    }
  }

  async uploadProductThumbnail(
    companyId: number,
    productId: number,
    file: File,
  ): Promise<any> {
    try {
      return await this.repository.uploadProductThumbnail(
        companyId,
        productId,
        file,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "썸네일을 업로드하지 못했습니다.",
        "PRODUCT_THUMBNAIL_UPLOAD_FAILED",
        error,
      );
    }
  }

  /**
   * 상품 삭제.
   *
   * 사용 중인 상품을 `confirmed` 없이 지우려 하면 삭제되지 않고 `deleted: false` 와
   * 사용처 요약이 돌아온다. 이건 에러가 아니라 재확인 대기 상태이므로 AppError 로 감싸지 않는다.
   * (AppError 는 원인을 String 으로 눌러 담아서 usages 같은 구조를 잃는다.)
   */
  async deleteProduct(
    companyId: number,
    productId: number,
    confirmed = false,
  ): Promise<DeleteProductResult> {
    try {
      return await this.repository.deleteProduct(companyId, productId, confirmed);
    } catch (error) {
      throw new AppError(
        String(error) || "상품을 삭제하지 못했습니다.",
        "PRODUCT_DELETE_FAILED",
        error,
      );
    }
  }

  async getProductPapers(
    companyId: number,
    productId: number,
  ): Promise<ProductPaperDto[]> {
    try {
      return await this.repository.getProductPapers(companyId, productId);
    } catch (error) {
      throw new AppError(
        String(error) || "상품 용지 목록을 불러오지 못했습니다.",
        "PRODUCT_PAPERS_GET_FAILED",
        error,
      );
    }
  }

  async createProductPaper(
    companyId: number,
    productId: number,
    request: CreateProductPaperRequest,
  ): Promise<ProductPaperDto> {
    try {
      return await this.repository.createProductPaper(
        companyId,
        productId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "상품 용지를 생성하지 못했습니다.",
        "PRODUCT_PAPER_CREATE_FAILED",
        error,
      );
    }
  }

  async updateProductPaper(
    companyId: number,
    productId: number,
    paperId: number,
    request: CreateProductPaperRequest,
  ): Promise<ProductPaperDto> {
    try {
      return await this.repository.updateProductPaper(
        companyId,
        productId,
        paperId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "상품 용지를 수정하지 못했습니다.",
        "PRODUCT_PAPER_UPDATE_FAILED",
        error,
      );
    }
  }

  async deleteProductPaper(
    companyId: number,
    productId: number,
    paperId: number,
  ): Promise<boolean> {
    try {
      return await this.repository.deleteProductPaper(
        companyId,
        productId,
        paperId,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "상품 용지를 삭제하지 못했습니다.",
        "PRODUCT_PAPER_DELETE_FAILED",
        error,
      );
    }
  }

  async getSizeTemplates(
    companyId: number,
    productId: number,
  ): Promise<SizeTemplateDto[]> {
    try {
      return await this.repository.getSizeTemplates(companyId, productId);
    } catch (error) {
      throw new AppError(
        String(error) || "사이즈 템플릿 목록을 불러오지 못했습니다.",
        "SIZE_TEMPLATES_GET_FAILED",
        error,
      );
    }
  }

  async createSizeTemplate(
    companyId: number,
    productId: number,
    request: CreateSizeTemplateRequest,
  ): Promise<SizeTemplateDto> {
    try {
      return await this.repository.createSizeTemplate(
        companyId,
        productId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "사이즈 템플릿을 생성하지 못했습니다.",
        "SIZE_TEMPLATE_CREATE_FAILED",
        error,
      );
    }
  }

  async updateSizeTemplate(
    companyId: number,
    productId: number,
    sizeTemplateId: number,
    request: CreateSizeTemplateRequest,
  ): Promise<SizeTemplateDto> {
    try {
      return await this.repository.updateSizeTemplate(
        companyId,
        productId,
        sizeTemplateId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "사이즈 템플릿을 수정하지 못했습니다.",
        "SIZE_TEMPLATE_UPDATE_FAILED",
        error,
      );
    }
  }

  async deleteSizeTemplate(
    companyId: number,
    productId: number,
    sizeTemplateId: number,
  ): Promise<boolean> {
    try {
      return await this.repository.deleteSizeTemplate(
        companyId,
        productId,
        sizeTemplateId,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "사이즈 템플릿을 삭제하지 못했습니다.",
        "SIZE_TEMPLATE_DELETE_FAILED",
        error,
      );
    }
  }

  async getOptions(
    companyId: number,
    productId: number,
  ): Promise<OptionDto[]> {
    try {
      return await this.repository.getOptions(companyId, productId);
    } catch (error) {
      throw new AppError(
        String(error) || "옵션 목록을 불러오지 못했습니다.",
        "OPTIONS_GET_FAILED",
        error,
      );
    }
  }

  async getOption(
    companyId: number,
    productId: number,
    optionId: number,
  ): Promise<OptionDto> {
    try {
      return await this.repository.getOption(companyId, productId, optionId);
    } catch (error) {
      throw new AppError(
        String(error) || "옵션 정보를 불러오지 못했습니다.",
        "OPTION_GET_FAILED",
        error,
      );
    }
  }

  async createOption(
    companyId: number,
    productId: number,
    request: CreateOptionRequest,
  ): Promise<OptionDto> {
    try {
      return await this.repository.createOption(
        companyId,
        productId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "옵션을 생성하지 못했습니다.",
        "OPTION_CREATE_FAILED",
        error,
      );
    }
  }

  async updateOption(
    companyId: number,
    productId: number,
    optionId: number,
    request: Partial<CreateOptionRequest>,
  ): Promise<OptionDto> {
    try {
      return await this.repository.updateOption(
        companyId,
        productId,
        optionId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "옵션을 수정하지 못했습니다.",
        "OPTION_UPDATE_FAILED",
        error,
      );
    }
  }

  async deleteOption(
    companyId: number,
    productId: number,
    optionId: number,
  ): Promise<boolean> {
    try {
      return await this.repository.deleteOption(
        companyId,
        productId,
        optionId,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "옵션을 삭제하지 못했습니다.",
        "OPTION_DELETE_FAILED",
        error,
      );
    }
  }

  async reorderOptionItems(
    companyId: number,
    productId: number,
    scope: OptionReorderScope,
    orderedIds: number[],
  ): Promise<void> {
    try {
      await this.repository.reorderOptionItems(companyId, productId, scope, orderedIds);
    } catch (error) {
      throw new AppError(
        error instanceof Error ? error.message : "순서를 저장하지 못했습니다.",
        "OPTION_REORDER_FAILED",
        error,
      );
    }
  }

  async addInsertPaper(
    companyId: number,
    productId: number,
  ): Promise<OptionDto> {
    try {
      return await this.repository.addInsertPaper(companyId, productId);
    } catch (error) {
      throw new AppError(
        String(error) || "간지를 추가하지 못했습니다.",
        "INSERT_PAPER_ADD_FAILED",
        error,
      );
    }
  }

  async addEndPaper(
    companyId: number,
    productId: number,
  ): Promise<OptionDto> {
    try {
      return await this.repository.addEndPaper(companyId, productId);
    } catch (error) {
      throw new AppError(
        String(error) || "면지를 추가하지 못했습니다.",
        "END_PAPER_ADD_FAILED",
        error,
      );
    }
  }

  async addPaperOrientation(
    companyId: number,
    productId: number,
  ): Promise<OptionDto> {
    try {
      return await this.repository.addPaperOrientation(companyId, productId);
    } catch (error) {
      throw new AppError(
        String(error) || "용지 방향을 추가하지 못했습니다.",
        "PAPER_ORIENTATION_ADD_FAILED",
        error,
      );
    }
  }

  async createOptionAttribute(
    companyId: number,
    productId: number,
    optionId: number,
    request: CreateOptionAttributeRequest,
  ): Promise<OptionAttributeDto> {
    try {
      return await this.repository.createOptionAttribute(
        companyId,
        productId,
        optionId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "옵션 속성을 생성하지 못했습니다.",
        "OPTION_ATTRIBUTE_CREATE_FAILED",
        error,
      );
    }
  }

  async updateOptionAttribute(
    companyId: number,
    productId: number,
    optionId: number,
    attributeId: number,
    request: CreateOptionAttributeRequest,
  ): Promise<OptionAttributeDto> {
    try {
      return await this.repository.updateOptionAttribute(
        companyId,
        productId,
        optionId,
        attributeId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "옵션 속성을 수정하지 못했습니다.",
        "OPTION_ATTRIBUTE_UPDATE_FAILED",
        error,
      );
    }
  }

  async deleteOptionAttribute(
    companyId: number,
    productId: number,
    optionId: number,
    attributeId: number,
  ): Promise<boolean> {
    try {
      return await this.repository.deleteOptionAttribute(
        companyId,
        productId,
        optionId,
        attributeId,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "옵션 속성을 삭제하지 못했습니다.",
        "OPTION_ATTRIBUTE_DELETE_FAILED",
        error,
      );
    }
  }

  async createOptionAttributeValue(
    companyId: number,
    productId: number,
    optionId: number,
    attributeId: number,
    request: CreateOptionAttributeValueRequest,
    imageFile?: File,
  ): Promise<OptionAttributeValueDto> {
    try {
      return await this.repository.createOptionAttributeValue(
        companyId,
        productId,
        optionId,
        attributeId,
        request,
        imageFile,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "옵션 값을 생성하지 못했습니다.",
        "OPTION_ATTRIBUTE_VALUE_CREATE_FAILED",
        error,
      );
    }
  }

  async updateOptionAttributeValue(
    companyId: number,
    productId: number,
    optionId: number,
    attributeId: number,
    valueId: number,
    request: CreateOptionAttributeValueRequest,
    imageFile?: File,
  ): Promise<OptionAttributeValueDto> {
    try {
      return await this.repository.updateOptionAttributeValue(
        companyId,
        productId,
        optionId,
        attributeId,
        valueId,
        request,
        imageFile,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "옵션 값을 수정하지 못했습니다.",
        "OPTION_ATTRIBUTE_VALUE_UPDATE_FAILED",
        error,
      );
    }
  }

  async deleteOptionAttributeValue(
    companyId: number,
    productId: number,
    optionId: number,
    attributeId: number,
    valueId: number,
  ): Promise<boolean> {
    try {
      return await this.repository.deleteOptionAttributeValue(
        companyId,
        productId,
        optionId,
        attributeId,
        valueId,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "옵션 값을 삭제하지 못했습니다.",
        "OPTION_ATTRIBUTE_VALUE_DELETE_FAILED",
        error,
      );
    }
  }

  async calculatePrice(
    companyId: number,
    productId: number,
    request: PriceCalculationRequest,
  ): Promise<PriceCalculationResult> {
    try {
      return await this.repository.calculatePrice(
        companyId,
        productId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "가격 계산에 실패했습니다.",
        "PRICE_CALCULATE_FAILED",
        error,
      );
    }
  }

  async getPaperSpecs(companyId: number, includeAutoGenerated?: boolean): Promise<PaperSpecDto[]> {
    try {
      return await this.repository.getPaperSpecs(companyId, includeAutoGenerated);
    } catch (error) {
      throw new AppError(
        String(error) || "용지 규격 목록을 불러오지 못했습니다.",
        "PAPER_SPECS_GET_FAILED",
        error,
      );
    }
  }

  async getMaterials(
    companyId: number,
    type?: string,
  ): Promise<MaterialDto[]> {
    try {
      return await this.repository.getMaterials(companyId, type);
    } catch (error) {
      throw new AppError(
        String(error) || "자재 목록을 불러오지 못했습니다.",
        "MATERIALS_GET_FAILED",
        error,
      );
    }
  }

  async getMaterial(
    companyId: number,
    materialId: number,
  ): Promise<MaterialDto> {
    try {
      return await this.repository.getMaterial(companyId, materialId);
    } catch (error) {
      throw new AppError(
        String(error) || "자재 정보를 불러오지 못했습니다.",
        "MATERIAL_GET_FAILED",
        error,
      );
    }
  }

  async createMaterial(
    companyId: number,
    request: { name: string; type: string; paperSpecId?: number; grammage?: number },
  ): Promise<MaterialDto> {
    try {
      return await this.repository.createMaterial(companyId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "자재를 생성하지 못했습니다.",
        "MATERIAL_CREATE_FAILED",
        error,
      );
    }
  }

  async updateMaterial(
    companyId: number,
    materialId: number,
    request: { name: string; paperSpecId?: number },
  ): Promise<MaterialDto> {
    try {
      return await this.repository.updateMaterial(
        companyId,
        materialId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "자재를 수정하지 못했습니다.",
        "MATERIAL_UPDATE_FAILED",
        error,
      );
    }
  }

  async deleteMaterial(
    companyId: number,
    materialId: number,
  ): Promise<boolean> {
    try {
      return await this.repository.deleteMaterial(companyId, materialId);
    } catch (error: any) {
      // 409 에러(자재 사용 중)인 경우 products를 포함한 에러를 그대로 전달
      if (error?.errorCode == 409 || error?.products) {
        throw error;
      }
      throw new AppError(
        error?.message || "자재를 삭제하지 못했습니다.",
        "MATERIAL_DELETE_FAILED",
        error,
      );
    }
  }
}

